// Campaign vs organic attribution (web only). Captures UTM tags on arrival,
// logs one visit per session, logs store-badge clicks site-wide via a single
// delegated click listener, and attributes new signups once.
import { supabase } from "@/integrations/supabase/client";
import { isNativePlatform } from "@/lib/platform";

const FN_URL = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/track-campaign`;
const ATTR_KEY = "dc_campaign_attr";
const VISIT_KEY = "dc_campaign_visit_logged";
const SIGNUP_KEY = "dc_campaign_signup_logged";

interface Attr { campaign?: string; source?: string; medium?: string }

function readAttr(): Attr {
  try { return JSON.parse(localStorage.getItem(ATTR_KEY) || "{}"); } catch { return {}; }
}

function captureFromUrl(): Attr {
  const p = new URLSearchParams(window.location.search);
  const fresh: Attr = {
    campaign: p.get("utm_campaign") || undefined,
    source: p.get("utm_source") || undefined,
    medium: p.get("utm_medium") || undefined,
  };
  if (fresh.campaign || fresh.source) {
    try { localStorage.setItem(ATTR_KEY, JSON.stringify(fresh)); } catch {}
    return fresh;
  }
  return readAttr(); // empty → organic
}

function send(payload: Record<string, unknown>, token?: string) {
  fetch(FN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

export function storeFromHref(href: string): "google_play" | "app_store" | null {
  if (href.includes("play.google.com/store/apps")) return "google_play";
  if (href.includes("apps.apple.com")) return "app_store";
  return null;
}

let started = false;
export function initCampaignAttribution() {
  if (started || isNativePlatform() || typeof window === "undefined") return;
  started = true;
  if (window.location.pathname.startsWith("/go/")) return; // redirect page logs its own event

  const attr = captureFromUrl();
  try {
    if (!sessionStorage.getItem(VISIT_KEY)) {
      sessionStorage.setItem(VISIT_KEY, "1");
      send({ event_type: "visit", ...attr, page_path: window.location.pathname, referrer: document.referrer || null });
    }
  } catch {}

  document.addEventListener("click", (e) => {
    const a = (e.target as HTMLElement | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a || a.closest("[data-preview-locked]")) return;
    const store = storeFromHref(a.href);
    if (!store) return;
    send({ event_type: "store_click", store, ...readAttr(), page_path: window.location.pathname });
  }, { capture: true });

  supabase.auth.onAuthStateChange((event, session) => {
    if (event !== "SIGNED_IN" || !session?.user) return;
    const created = new Date(session.user.created_at).getTime();
    if (Date.now() - created > 15 * 60 * 1000) return; // only brand-new accounts
    const key = `${SIGNUP_KEY}_${session.user.id}`;
    try { if (localStorage.getItem(key)) return; localStorage.setItem(key, "1"); } catch {}
    send({ event_type: "signup", ...readAttr(), page_path: window.location.pathname }, session.access_token);
  });
}
